import { useEffect, useState, type ReactNode } from 'react';
import { Field } from './Form';

/**
 * Confirm-with-reason modal. Replaces the ~140 lines of duplicated ban / unban
 * modal JSX that previously lived in `routes/users/Detail.tsx`. Designed for
 * admin actions that require a free-text rationale recorded in the audit log
 * (ban, unban, future: suspend-account, revoke-iap, etc.).
 *
 * Validation is minimal on purpose — the server enforces the real rules. We
 * require a 3-char minimum only to stop the trivial "fat-fingered confirm"
 * case where the user hits "Confirm" with an empty textarea.
 *
 * `tone="danger"` paints the confirm button red (ban, delete); `tone="safe"`
 * paints it emerald (unban, restore). Callers supply their own label so the
 * verb reads correctly ("Ban user", "Unban user", "Revoke IAP").
 */

export interface ReasonedConfirmModalProps {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  /** Palette of the confirm button. */
  tone?: 'danger' | 'safe';
  /** `true` while the onConfirm promise resolves. */
  busy?: boolean;
  /** Minimum reason length — server default is 3. */
  minReasonLength?: number;
  /** Textarea placeholder — should reflect expected rationale. */
  reasonPlaceholder?: string;
  /** Helper text shown under the textarea. */
  reasonHint?: string;
  /** Optional testId for the modal wrapper; confirm/cancel buttons append `-confirm` / `-cancel`. */
  testId?: string;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
}

export default function ReasonedConfirmModal({
  open,
  title,
  description,
  confirmLabel,
  tone = 'danger',
  busy = false,
  minReasonLength = 3,
  reasonPlaceholder,
  reasonHint = 'Recorded in the audit log.',
  testId = 'reasoned-confirm-modal',
  onConfirm,
  onCancel,
}: ReasonedConfirmModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset state when the modal closes so a cancelled ban doesn't leak its
  // reason into the next unban invocation. Previously the hooks retained
  // their values across open/close cycles, which confused admins who
  // reopened the modal and found stale text prefilled.
  useEffect(() => {
    if (!open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  function handleConfirm() {
    const trimmed = reason.trim();
    if (trimmed.length < minReasonLength) {
      setError(
        `A reason of at least ${minReasonLength} characters is required.`,
      );
      return;
    }
    setError(null);
    void onConfirm(trimmed);
  }

  function handleCancel() {
    if (busy) return;
    setReason('');
    setError(null);
    onCancel();
  }

  const confirmClass =
    tone === 'danger'
      ? 'bg-red-600 text-white hover:bg-red-500'
      : 'bg-emerald-600 text-white hover:bg-emerald-500';

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-title`}
      data-testid={testId}
    >
      <div
        className="absolute inset-0 bg-slate-900/50"
        data-testid={`${testId}-backdrop`}
        onClick={handleCancel}
      />
      <div className="relative bg-white rounded-lg shadow-lg max-w-md w-full p-5 space-y-3">
        <h2
          id={`${testId}-title`}
          className="text-base font-semibold text-slate-900"
        >
          {title}
        </h2>
        <p className="text-sm text-slate-600">{description}</p>
        <Field
          label="Reason"
          htmlFor={`${testId}-reason`}
          required
          error={error}
          hint={reasonHint}
        >
          <textarea
            id={`${testId}-reason`}
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
            placeholder={reasonPlaceholder}
            data-testid={`${testId}-input`}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
            disabled={busy}
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={busy}
            data-testid={`${testId}-cancel`}
            className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            data-testid={`${testId}-confirm`}
            className={`text-sm rounded px-3 py-1.5 disabled:opacity-50 ${confirmClass}`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
